import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { api, ApiError } from './api.js';
import { authMode, clerkConfigured, currentIdentity, devIdentities, getSessionToken, signInAsDevIdentity, signOut, startClerkSignIn } from './auth.js';
import { Card, ErrorBanner, Loading, SessionContext, ToastHost, useSession } from './ui.jsx';
import MemberHome from './pages/member-home.jsx';
import ItemDetail from './pages/item-detail.jsx';
import EquipmentPage from './pages/equipment.jsx';
import ConsoleOverview from './pages/console-overview.jsx';
import ConsoleHistory from './pages/console-history.jsx';
import IssueItemPage from './pages/issue-item.jsx';
import BalancesPage from './pages/balances.jsx';
import RecurrencesPage from './pages/recurrences.jsx';
import MembersPage from './pages/members.jsx';
import HistoricalPage from './pages/historical.jsx';
import ConfirmationsPage from './pages/confirmations.jsx';
import './styles.css';

const ROUTES = [
  { path: '/', component: MemberHome, title: '我的款項' },
  { path: '/equipment', component: EquipmentPage, title: '單位與設備' },
  { path: '/confirmations', component: ConfirmationsPage, title: '待我確認' },
  { path: '/items/:id', component: ItemDetail, title: '付款項目' },
  { path: '/console', component: ConsoleOverview, adminOnly: true, title: '成員分攤主頁' },
  { path: '/console/history', component: ConsoleHistory, adminOnly: true, title: '帳簿歷史' },
  { path: '/console/issue', component: IssueItemPage, adminOnly: true, title: '發出付款項目' },
  { path: '/console/balances', component: BalancesPage, adminOnly: true, title: '待處理結餘' },
  { path: '/console/recurrences', component: RecurrencesPage, adminOnly: true, title: '循環設定' },
  { path: '/console/members', component: MembersPage, adminOnly: true, title: '成員與角色' },
  { path: '/console/historical', component: HistoricalPage, adminOnly: true, title: '歷史補錄' },
];

function matchRoute(pathname) {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  for (const route of ROUTES) {
    const segments = route.path.split('/').filter(Boolean);
    if (segments.length !== parts.length) continue;
    const params = {};
    let matched = true;
    for (let index = 0; index < segments.length; index += 1) {
      if (segments[index].startsWith(':')) params[segments[index].slice(1)] = decodeURIComponent(parts[index]);
      else if (segments[index] !== parts[index]) { matched = false; break; }
    }
    if (matched) return { route, params };
  }
  return null;
}

// Every component that reads the location subscribes here, so a single
// navigation updates the shell and the routed page together.
const locationListeners = new Set();

export function navigate(path) {
  window.history.pushState({}, '', path);
  for (const listener of locationListeners) listener();
}

export function Link({ to, children, ...rest }) {
  return (
    <a
      href={to}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey) return;
        event.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

function useLocation() {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    locationListeners.add(update);
    window.addEventListener('popstate', update);
    return () => {
      locationListeners.delete(update);
      window.removeEventListener('popstate', update);
    };
  }, []);
  return pathname;
}

function SignIn() {
  const [identities, setIdentities] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authMode !== 'dev') return;
    devIdentities().then(setIdentities).catch(setError);
  }, []);

  return (
    <div className="signin">
      <Card title="夾租簿 · 合租帳務" subtitle="只限獲邀成員登入" testId="sign-in">
        <ErrorBanner error={error} />
        {authMode === 'clerk' ? (
          clerkConfigured ? (
            <div className="stack">
              <p className="small muted">使用 Google 帳戶或 Magic Link 登入。未獲邀的帳戶無法讀取合租群組資料。</p>
              <button type="button" className="primary" onClick={() => startClerkSignIn('oauth')} data-testid="sign-in-clerk">
                使用 Google 或 Magic Link 登入
              </button>
            </div>
          ) : (
            // Deployed before the identity provider was configured: say so plainly
            // instead of offering a button that cannot work.
            <div className="banner warn small" data-testid="sign-in-unconfigured">
              此環境尚未完成身分設定，暫時無法登入。管理員需在部署設定中填入 Clerk publishable key。
            </div>
          )
        ) : (
          <div className="stack">
            <p className="small muted">
              本機開發身分提供者（正式環境為 Clerk）。選擇身分即可取得 session token。
            </p>
            <div className="identity-list">
              {(identities ? identities.members : []).map((identity) => (
                <button
                  type="button"
                  key={identity.auth_subject}
                  data-testid={`sign-in-${identity.email}`}
                  onClick={async () => {
                    await signInAsDevIdentity(identity);
                    window.location.assign('/');
                  }}
                >
                  <span>{identity.display_name}</span>
                  <span className="small muted">{identity.role === 'admin' ? '管理員' : '一般成員'}</span>
                </button>
              ))}
              {(identities ? identities.uninvited : []).map((identity) => (
                <button
                  type="button"
                  key={identity.auth_subject}
                  data-testid={`sign-in-${identity.email}`}
                  onClick={async () => {
                    await signInAsDevIdentity(identity);
                    window.location.assign('/');
                  }}
                >
                  <span>{identity.display_name}</span>
                  <span className="small muted">未獲邀</span>
                </button>
              ))}
              {!identities ? <Loading /> : null}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function Shell({ children }) {
  const session = useSession();
  const pathname = useLocation();
  const isAdmin = session.member.role === 'admin';
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <strong>夾租簿</strong>
          <small>{session.settings.group_name}</small>
        </div>
        <nav aria-label="主導覽">
          <a href="/" aria-current={pathname === '/' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/'); }} data-testid="nav-home">我的款項</a>
          <a href="/equipment" aria-current={pathname === '/equipment' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/equipment'); }} data-testid="nav-equipment">單位與設備</a>
          <a href="/confirmations" aria-current={pathname === '/confirmations' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/confirmations'); }} data-testid="nav-confirmations">待我確認</a>
          {isAdmin ? (
            <>
              <div className="nav-group">帳簿控制台</div>
              <a href="/console" aria-current={pathname === '/console' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/console'); }} data-testid="nav-console">成員分攤主頁</a>
              <a href="/console/history" aria-current={pathname === '/console/history' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/console/history'); }} data-testid="nav-history">帳簿歷史與匯出</a>
              <a href="/console/issue" aria-current={pathname === '/console/issue' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/console/issue'); }} data-testid="nav-issue">發出付款項目</a>
              <a href="/console/balances" aria-current={pathname === '/console/balances' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/console/balances'); }} data-testid="nav-balances">待處理結餘</a>
              <a href="/console/recurrences" aria-current={pathname === '/console/recurrences' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/console/recurrences'); }} data-testid="nav-recurrences">循環設定</a>
              <a href="/console/historical" aria-current={pathname === '/console/historical' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/console/historical'); }} data-testid="nav-historical">歷史補錄</a>
              <a href="/console/members" aria-current={pathname === '/console/members' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('/console/members'); }} data-testid="nav-members">成員與角色</a>
            </>
          ) : null}
        </nav>
        <div className="sidebar-footer">
          <div>
            <strong data-testid="current-member">{session.member.display_name}</strong>
            <div className="small muted" data-testid="current-role">{isAdmin ? '管理員' : '一般成員'}</div>
          </div>
          <button type="button" className="small" onClick={signOut} data-testid="sign-out">登出</button>
        </div>
      </aside>
      <main className="content">{children}</main>
    </div>
  );
}

function App() {
  const [identity, setIdentity] = useState(undefined);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const pathname = useLocation();

  useEffect(() => {
    currentIdentity().then(setIdentity).catch(() => setIdentity(null));
  }, []);

  const loadSession = useCallback(async () => {
    if (!identity) return;
    try {
      const token = await getSessionToken();
      if (!token) { setIdentity(null); return; }
      setSession(await api.session());
      setError(null);
    } catch (failure) {
      setError(failure);
    }
  }, [identity]);

  useEffect(() => { loadSession(); }, [loadSession]);

  const match = useMemo(() => matchRoute(pathname), [pathname]);

  if (identity === undefined) return <div className="signin"><Loading /></div>;
  if (!identity) return <SignIn />;

  if (error) {
    const refused = error instanceof ApiError && (error.status === 401 || error.status === 403);
    return (
      <div className="signin">
        <Card title={refused ? '未獲邀加入合租群組' : '無法載入帳簿'} testId="access-denied">
          <ErrorBanner error={error} />
          <p className="small muted">
            {refused ? '此帳戶未獲邀，因此不能讀取合租群組資料。請聯絡管理員取得邀請。' : '請稍後再試。'}
          </p>
          <div className="modal-actions">
            <button type="button" onClick={signOut} data-testid="sign-out">登出</button>
          </div>
        </Card>
      </div>
    );
  }

  if (!session) return <div className="signin"><Loading label="載入帳簿…" /></div>;

  const Component = match ? match.route.component : null;
  const blocked = match && match.route.adminOnly && session.member.role !== 'admin';

  return (
    <SessionContext.Provider value={{ ...session, reloadSession: loadSession }}>
      <Shell>
        {!match ? (
          <Card title="找不到頁面" testId="not-found">
            <p className="small muted">此頁面不存在。</p>
          </Card>
        ) : blocked ? (
          <Card title="沒有權限" testId="console-blocked">
            <p className="small muted">只有管理員可以進入帳簿控制台。</p>
          </Card>
        ) : (
          <Component params={match.params} />
        )}
      </Shell>
    </SessionContext.Provider>
  );
}

createRoot(document.getElementById('root')).render(
  <ToastHost>
    <App />
  </ToastHost>,
);
